import { t } from "../../i18n";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatTl, type PaymentDto } from "@tasduvar/shared";
import { api, errorMessage } from "../../api/client";
import { useProvider } from "../../provider/useProvider";
import { useAuth } from "../../auth/useAuth";
import { ProfileSection } from "../../auth/ProfileSection";
import { PasswordField } from "../../auth/AuthLayout";
import { BillingCard } from "../../billing/BillingCard";
import { CadFormContext } from "../../ui/formContext";
import { Button } from "../../ui/Button";
import { CadShell } from "../cad/CadShell";
import { UsersTab } from "./UsersTab";
import { PaymentsTab } from "./PaymentsTab";
import { DefaultsTab, FeaturesTab, GeneralTab, LogTab, TransferTab } from "./AdminPage";

export type OptionsCategory = "users" | "payments" | "account" | "subscription" | "general" | "defaults" | "features" | "log" | "transfer";
/** Kategori sırası (README §4.8); `props` = gövde özellik ızgarası (TextField/NumberField/Toggle satır olur), diğerleri tablo/serbest gövde */
const CATS: { id: OptionsCategory; label: string; props?: boolean }[] = [
  { id: "users", label: t("Kullanıcılar") }, { id: "payments", label: t("Ödemeler") }, { id: "account", label: t("Hesap"), props: true }, { id: "subscription", label: t("Abonelik") },
  { id: "general", label: t("Genel"), props: true }, { id: "defaults", label: t("Varsayılanlar"), props: true }, { id: "features", label: t("Özellikler"), props: true }, { id: "log", label: t("Günlük") }, { id: "transfer", label: t("Aktar") },
];
const PAY_STATUS: Record<PaymentDto["status"], string> = { pending: "Bekliyor", paid: "Ödendi", failed: "Başarısız" };

/** Seçenekler penceresi çerçevesi (README §3 "Seçenekler"): 26 px başlık çubuğu + sol 200 px kategori listesi + sağ gövde; kabuk içinde ortalı. */
export function OptionsWindow({ title, children, onClose, footer }: { title: string; children: ReactNode; onClose: () => void; footer?: ReactNode }): JSX.Element {
  return (
    <div className="flex-1 overflow-auto p-5 bg-cad-off min-h-0">
      <div className="cad-options max-w-[1000px] mx-auto bg-cad-panel border border-cad-strong shadow-[0_14px_30px_-14px_rgb(0_0_0/0.4)] flex flex-col min-h-[60vh]">
        <div className="h-[26px] shrink-0 flex items-center gap-2 px-2 bg-cad-head border-b border-cad-line text-[11px] font-semibold text-cad-ink2">
          <span>{title}</span>
          <button type="button" aria-label={t("Pencereyi kapat")} title={t("Pencereyi kapat")} onClick={onClose} className="ml-auto w-4 h-4 inline-flex items-center justify-center rounded-[2px] text-cad-ink3 hover:bg-cad-hover hover:text-cad-ink cursor-pointer">✕</button>
        </div>
        {children}
        {footer ? <CadFormContext.Provider value={true}><div className="shrink-0 flex items-center justify-end gap-2 px-2 py-2 bg-cad-sub border-t border-cad-line">{footer}</div></CadFormContext.Provider> : null}
      </div>
    </div>
  );
}

/** Hesap kategorisi: profil/fatura (ProfileSection), ödeme sonucu, hesabı sil (README §4.2) */
function AccountBody(): JSX.Element {
  const nav = useNavigate();
  const { deleteAccount } = useAuth();
  const [pw, setPw] = useState(""); const [err, setErr] = useState<string>();
  const odeme = new URLSearchParams(window.location.search).get("odeme");
  return (
    <>
      {odeme === "ok" ? <p role="status" className="px-2 py-1 text-[11px] text-[#1D7A46] bg-[#1D7A46]/10 border-b border-cad-soft2">{t("Ödemeniz alındı, planınız etkinleştirildi.")}</p> : odeme === "hata" ? <p role="alert" className="px-2 py-1 text-[11px] text-[#B42318] bg-cad-badbg border-b border-cad-soft2">{t("Ödeme tamamlanamadı. Tekrar deneyebilir ya da havale ile ödeyebilirsiniz.")}</p> : null}
      <ProfileSection />
      <section className="grid gap-1" aria-label={t("Hesabı sil")}>
        <h2 className="!text-[#B42318]">{t("Hesabı sil")}</h2>
        <p className="text-[11px] text-cad-ink3">{t("Hesabınız, projeleriniz ve paylaşım bağlantılarınız kalıcı olarak silinir (KVKK). Önce projelerinizi JSON olarak indirmenizi öneririz.")}</p>
        <PasswordField id="del-pw" label={t("Parola (Google hesabında boş)")} value={pw} onChange={setPw} autoComplete="current-password" />
        <Button variant="danger" onClick={() => { if (!window.confirm("Hesabınız ve tüm projeleriniz silinecek. Emin misiniz?")) return; deleteAccount(pw || undefined).then(() => nav("/")).catch((ex: unknown) => setErr(errorMessage(ex))); }}>{t("Hesabı sil")}</Button>
        {err ? <p role="alert" className="text-[11px] text-[#B42318]">{err}</p> : null}
      </section>
    </>
  );
}

/** Abonelik kategorisi: durum paneli + ödeme geçmişi CadTable + Planlar (README §4.2) */
function SubscriptionBody(): JSX.Element {
  const { subscription, status } = useAuth();
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  useEffect(() => { if (status === "authed") void api<PaymentDto[]>("GET", "/billing/payments").then(setPayments).catch(() => undefined); }, [status]);
  if (!subscription) return <p className="p-3 text-[11px] text-cad-ink3">{t("Yükleniyor…")}</p>;
  return (
    <div className="grid gap-3 p-2">
      <BillingCard sub={subscription} />
      <section aria-label={t("Ödemeler")} className="grid gap-1">
        <h2>{t("Ödemeler")}</h2>
        {payments.length === 0 ? <p className="text-[11px] text-cad-ink3">{t("Henüz ödeme yok.")}</p> : (
          <table className="w-full text-[11px] border-collapse"><thead><tr><th>{t("Tarih")}</th><th>{t("Plan")}</th><th className="text-right">{t("Tutar")}</th><th>{t("Yöntem")}</th><th>{t("Referans")}</th><th>{t("Durum")}</th></tr></thead>
            <tbody>{payments.map((p) => <tr key={p.id}><td className="font-mono">{new Date(p.createdAt).toLocaleDateString("tr-TR")}</td><td>{p.planCode}</td><td className="font-mono text-right">{formatTl(p.amountKurus)}</td><td>{p.method === "transfer" ? t("Havale") : "iyzico"}</td><td className="font-mono">{p.reference ?? "—"}</td><td>{t(PAY_STATUS[p.status])}</td></tr>)}</tbody></table>
        )}
      </section>
    </div>
  );
}

/**
 * Seçenekler penceresi (README §3, §4.2, §4.8): sağlayıcı paneli sekmeleri + Hesap + Abonelik tek pencerede; kategori listesi yetkiye göre süzülür.
 * Sunucu sürümü: yönetici → tümü; giriş yapmış kullanıcı → Hesap, Abonelik; misafir → uyarı penceresi. Statik sürüm: parola penceresi → Genel…Aktar.
 * Erişilebilir adlar korunur: kategori `tab` rolü ("Özellikler", "Günlük"…), parola formu "Sağlayıcı girişi" / "Parola" / "Gir".
 */
export function OptionsPage({ initial }: { initial?: OptionsCategory } = {}): JSX.Element {
  const nav = useNavigate();
  const { authed, login, logout, load, loaded, serverMode } = useProvider();
  const auth = useAuth();
  const [pw, setPw] = useState(""); const [err, setErr] = useState<string>();
  useEffect(() => { if (!loaded) void load(); }, [loaded, load]);
  const isAdmin = serverMode ? auth.user?.role === "admin" : authed;
  const cats = CATS.filter((c) => {
    if (c.id === "users" || c.id === "payments") return serverMode && isAdmin;
    if (c.id === "account" || c.id === "subscription") return serverMode && auth.status === "authed";
    return isAdmin;
  });
  const [cat, setCat] = useState<OptionsCategory>(initial ?? "general");
  const active = cats.some((c) => c.id === cat) ? cat : cats[0]?.id;
  const close = (): void => { nav("/"); };
  const title = serverMode && auth.status === "authed" && !isAdmin ? t("Seçenekler — Hesabım") : t("Seçenekler — Sağlayıcı paneli");

  let body: ReactNode;
  if (serverMode && auth.status === "unknown") body = <p className="p-6 text-cad-ink3">{t("Yükleniyor…")}</p>;
  else if (serverMode && auth.status !== "authed") {
    // Misafir: giriş uyarısı (metinler AdminPage'den)
    body = (
      <OptionsWindow title={t("Sağlayıcı paneli")} onClose={close} footer={<><Link to="/" className="cad-btn-box">{t("← Giriş")}</Link><Link to="/giris" className="cad-btn-primary">{t("Giriş yap")}</Link></>}>
        <div className="p-3 text-[11px]" role="alert">{t("Yönetici hesabıyla giriş yapın.")}</div>
      </OptionsWindow>
    );
  } else if (!serverMode && !authed) {
    body = (
      <OptionsWindow title={t("Sağlayıcı paneli")} onClose={close}>
        <form className="p-3 grid gap-2 max-w-sm" onSubmit={(e) => { e.preventDefault(); void login(pw).then((ok) => setErr(ok ? undefined : "Parola hatalı")); }} aria-label={t("Sağlayıcı girişi")}>
          <label htmlFor="adm-pw" className="text-[11px] text-cad-ink2">{t("Parola")}</label>
          <input id="adm-pw" type="password" className="cad-input font-sans" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus />
          {err ? <p role="alert" className="text-[11px] text-[#B42318]">{err}</p> : null}
          <p className="text-[10px] text-cad-ink3">{t("Tarayıcıda saklanan ayar ve parola gerçek bir güvenlik sınırı değildir; yanlışlıkla değişikliği önlemek içindir.")}</p>
          <div className="flex justify-between"><Link to="/" className="cad-btn-box">{t("← Giriş")}</Link><Button type="submit">{t("Gir")}</Button></div>
        </form>
      </OptionsWindow>
    );
  } else {
    const def = cats.find((c) => c.id === active);
    const content = active === "users" ? <UsersTab /> : active === "payments" ? <PaymentsTab /> : active === "account" ? <AccountBody /> : active === "subscription" ? <SubscriptionBody />
      : active === "general" ? <GeneralTab /> : active === "defaults" ? <DefaultsTab /> : active === "features" ? <FeaturesTab /> : active === "log" ? <LogTab /> : <TransferTab />;
    body = (
      <OptionsWindow title={title} onClose={close} footer={<>{serverMode ? <span className="mr-auto text-[10px] text-cad-ink3 font-mono">{t("sunucu ·")} {auth.user?.email}</span> : <Button variant="ghost" className="mr-auto" onClick={logout}>{t("Çıkış")}</Button>}<Link to="/" className="cad-btn-box">{t("Uygulama")}</Link></>}>
        <div className="flex-1 grid grid-cols-[200px_minmax(0,1fr)] min-h-0">
          <ul role="tablist" aria-label={t("Kategoriler")} aria-orientation="vertical" className="m-0 p-1.5 list-none border-r border-cad-line grid gap-px content-start">
            {cats.map((c) => <li key={c.id}><button type="button" role="tab" aria-selected={c.id === active} aria-controls={`panel-${c.id}`} onClick={() => setCat(c.id)} className="w-full text-left px-2 py-1 text-[12px] rounded-[2px] cursor-pointer text-cad-ink2 hover:bg-cad-hover aria-selected:bg-cad-select aria-selected:text-cad-accent aria-selected:font-medium">{c.label}</button></li>)}
          </ul>
          <div role="tabpanel" id={`panel-${active}`} className="min-w-0 bg-white flex flex-col">
            <div className="cad-head">{def?.label}</div>
            <CadFormContext.Provider value={true}>
              <div className={`cad-props flex-1 min-h-0 ${def?.props ? "" : "cad-options-free"}`}><div className={def?.props ? "cad-props-body grid" : "p-2 grid gap-2 content-start"}>{content}</div></div>
            </CadFormContext.Provider>
          </div>
        </div>
      </OptionsWindow>
    );
  }
  return <CadShell current="options">{body}</CadShell>;
}
